import React, { useState, useEffect, useMemo } from 'react';
import { getOnboardingUrl } from "../../utils/portalUrl";
import { fetchSubscriptionPlans, resolveDisplayPrice, getPlanBenefits } from "../../utils/plansApi";
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import SEO from '../../Components/SEO/SEO';
import { PRICING_META } from '../../seo/pageMeta';
import { trackViewPricing } from '../../utils/track';
import styles from './Pricing.module.css';
import { filterPlanBenefits, TEMP_CRM_PIVOT } from '../../config/tempCrmPivot';

// Enterprise is a contact-sales tier — not stored in SubscriptionPlan
// because it has no fixed price and isn't self-serve checkoutable.
// Rendered as a static appended card with a mailto: CTA.
const ENTERPRISE_CONTACT_EMAIL = 'hello@stylon.co';

const PricingPage = () => {
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('MONTHLY');
  /**
   * THE CARDS TURN OVER WHEN THE PRICE CHANGES.
   *
   * The owner asked for "some animation of flipping card" on the monthly/yearly
   * switch, and it earns its keep: the only thing that changes is a number in
   * the middle of a long card, which is easy to miss entirely. The flip says
   * "these are different prices now" without a word of copy.
   *
   * A counter rather than a boolean, because the same period can be re-picked
   * and CSS only replays an animation when the element is new to it — the key
   * below changes on every flip, so React gives it a fresh node each time.
   */
  const [flipCount, setFlipCount] = useState(0);
  const pickPeriod = (period) => {
    setBillingPeriod(period);
    setFlipCount((n) => n + 1);
  };
  // null = still loading; [] = loaded but empty (admin hasn't created plans);
  // [...plans] = loaded.
  const [apiPlans, setApiPlans] = useState(null);
  const faqs = t('pricing.faqs', { returnObjects: true });

  /**
   * FAQPage from the very list this page renders.
   *
   * PRICING_META used to hand-write four different questions, none of which
   * appeared on the page. Google requires FAQ content to be visible, so the
   * markup has to come from the same source the section reads.
   */
  const faqJsonLd = Array.isArray(faqs) && faqs.length ? [{
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q || item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.a || item.answer },
    })),
  }] : [];

  // Fire GTM view_pricing once on mount — top-of-funnel engagement signal that
  // strengthens Google Ads audiences (visitors who saw plans = high-intent).
  useEffect(() => { trackViewPricing(); }, []);

  useEffect(() => {
    let cancelled = false;
    const language = (i18n.language || 'en').split('-')[0];
    fetchSubscriptionPlans(language).then((plans) => {
      if (cancelled) return;
      setApiPlans(plans);
    });
    return () => { cancelled = true; };
  }, [i18n.language]);

  // The trial plan is auto-assigned during onboarding (admin defines it
  // with `isTrial: true`); it isn't a buyable tier so we don't render it
  // as a card. We DO surface its `trialDays` in the banner above the grid
  // so visitors know exactly how long they get to try Stylon for free.
  const buyablePlans = useMemo(() => {
    if (!Array.isArray(apiPlans)) return [];
    return apiPlans.filter((p) => !p.isTrial);
  }, [apiPlans]);

  const trialDays = useMemo(() => {
    if (!Array.isArray(apiPlans)) return null;
    const trial = apiPlans.find((p) => p.isTrial && Number(p.trialDays) > 0);
    return trial ? Number(trial.trialDays) : null;
  }, [apiPlans]);

  // Highest yearly-vs-monthly saving across the buyable plans (0 = no discount).
  // Drives the compact "Save N%" badge on the YEARLY toggle button.
  const maxYearlySavingsPercent = useMemo(() => {
    let max = 0;
    for (const p of buyablePlans) {
      const m = Number(p?.monthlyPrice ?? 0);
      const y = p?.yearlyPrice != null && p.yearlyPrice !== '' ? Number(p.yearlyPrice) : m;
      if (m > 0 && y > 0 && y < m) {
        const pct = Math.round(((m - y) / m) * 100);
        if (pct > max) max = pct;
      }
    }
    return max;
  }, [buyablePlans]);

  const cards = useMemo(() => {
    return buyablePlans.map((plan) => {
      const price = resolveDisplayPrice(plan, billingPeriod);
      const benefits = getPlanBenefits(plan);
      const rawFeatures = Array.isArray(benefits)
        ? benefits.map((b) => (typeof b === 'string' ? b : (b?.text || b?.label || ''))).filter(Boolean)
        : [];
      return {
        key: plan.id,
        planId: plan.id,
        // What the card is debited, by the server's rule (subscriptions/pricing.ts
        // computeSubscriptionCharge): a GROSS plan (the default) charges its price
        // with VAT inside it; a NET plan adds VAT on top. The label must follow.
        vatIncluded: plan.priceMode !== 'NET',
        isPopular: !!plan.isHighlighted,
        name: plan.name || plan.nameEn,
        tagline: TEMP_CRM_PIVOT ? '' : (plan.description || plan.descriptionEn || ''),
        price,
        features: TEMP_CRM_PIVOT ? [] : filterPlanBenefits(rawFeatures),
        ctaLabel: price.isFree
          ? t('pricing.getStarted', { defaultValue: 'Get Started' })
          : t('pricing.startNow', { defaultValue: 'Start Now' }),
      };
    });
  }, [buyablePlans, billingPeriod, t]);

  const enterpriseFeatures = t('pricing.enterpriseFeatures', { returnObjects: true });
  const enterpriseMailto = `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=${encodeURIComponent('Stylon Enterprise inquiry')}`;

  return (
    <div className={styles.page}>
      <SEO {...PRICING_META} jsonLd={[...(PRICING_META.jsonLd || []), ...faqJsonLd]} />
      <Navbar />

      {/* Hero */}
      <section className={styles.hero}>
        <div className="container">
          <h1 data-aos="fade-up">{t('pricing.heroTitle', { defaultValue: 'Pricing' })}</h1>
          <p data-aos="fade-up" data-aos-delay="80">{t('pricing.heroSubtitle', { defaultValue: 'Choose the plan that fits your business.' })}</p>
        </div>
      </section>

      {/* Plans */}
      <section className={styles.plansSection}>
        <div className="container">
          {trialDays && (
            <div className={styles.trialBanner} data-testid="trial-banner">
              {t('pricing.trialBanner', {
                defaultValue: 'Every plan starts with a {{days}}-day free trial — no credit card required.',
                days: trialDays,
              })}
            </div>
          )}

          {maxYearlySavingsPercent > 0 && (
            <div className={styles.billingToggle} role="tablist" aria-label="Billing period">
              <button
                type="button"
                role="tab"
                aria-selected={billingPeriod === 'MONTHLY'}
                className={`${styles.billingToggleBtn} ${billingPeriod === 'MONTHLY' ? styles.billingToggleBtnActive : ''}`}
                onClick={() => pickPeriod('MONTHLY')}
                data-testid="billing-toggle-monthly"
              >
                {t('pricing.billingToggleMonthly', { defaultValue: 'Monthly' })}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={billingPeriod === 'YEARLY'}
                className={`${styles.billingToggleBtn} ${billingPeriod === 'YEARLY' ? styles.billingToggleBtnActive : ''}`}
                onClick={() => pickPeriod('YEARLY')}
                data-testid="billing-toggle-yearly"
              >
                {t('pricing.billingToggleYearly', { defaultValue: 'Yearly' })}
                <span className={styles.billingToggleSavings} data-testid="yearly-savings-badge">
                  −{maxYearlySavingsPercent}%
                </span>
              </button>
            </div>
          )}

          {apiPlans === null && (
            <p className={styles.pricingLoading} data-testid="pricing-loading">
              {t('pricing.loading', { defaultValue: 'Loading plans…' })}
            </p>
          )}
          {apiPlans !== null && cards.length === 0 && (
            <p className={styles.pricingEmpty} data-testid="pricing-empty">
              {t('pricing.empty', { defaultValue: 'Pricing details are coming soon.' })}
            </p>
          )}

          <div className={styles.plansGrid}>
            {cards.map((card, i) => {
              // CTAs pre-select plan + period so the salon-portal onboarding
              // wizard hands the visitor off to /membership/checkout/<id> at
              // the end of the flow with the period they picked here.
              const href = getOnboardingUrl(`pricing_${card.key}`, { planId: card.planId, period: billingPeriod });
              const showStrike = !card.price.isFree && billingPeriod === 'YEARLY' && card.price.percentSaved > 0;
              return (
                <div
                  key={`${card.key}-${flipCount}`}
                  className={`${styles.planCard} ${card.isPopular ? styles.popular : ''} ${flipCount > 0 ? styles.planCardFlip : ''}`}
                  style={flipCount > 0 ? { animationDelay: `${i * 70}ms` } : undefined}
                  data-aos="fade-up"
                  data-aos-delay={i * 100}
                  data-testid={`plan-card-${card.planId}`}
                >
                  {card.isPopular && (
                    <div className={styles.popularBadge}>{t('pricing.mostPopular')}</div>
                  )}
                  <div className={styles.planHeader}>
                    <h3>{card.name}</h3>
                    {!TEMP_CRM_PIVOT && card.tagline && <p className={styles.planTagline}>{card.tagline}</p>}
                    <div className={styles.priceRow}>
                      {card.price.isFree ? (
                        <span className={styles.price}>{t('pricing.priceFree', { defaultValue: 'Free' })}</span>
                      ) : (
                        <>
                          {showStrike && (
                            <span className={styles.priceStrike}>{card.price.symbol}{card.price.monthlyAmount}</span>
                          )}
                          <span className={styles.price}>{card.price.symbol}{card.price.amount}</span>
                          <span className={styles.period}>
                            {t('pricing.perMonth', { defaultValue: '/month' })}
                          </span>
                        </>
                      )}
                    </div>
                    {!card.price.isFree && (
                      <div className={styles.priceMeta}>
                        <span className={styles.priceVat}>
                          {card.vatIncluded
                            ? t('pricing.inclVat', { defaultValue: 'incl. VAT' })
                            : t('pricing.plusVat', { defaultValue: '+ VAT' })}
                        </span>
                        {billingPeriod === 'YEARLY' && (
                          <span className={styles.priceAnnualNote}>
                            {t('pricing.billedAnnually', { defaultValue: 'billed annually' })} ({card.price.symbol}{card.price.yearlyTotalAmount}/yr)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {!TEMP_CRM_PIVOT && card.features.length > 0 && (
                  <ul className={styles.featureList}>
                    {card.features.map((f, idx) => (
                      <li key={idx}>
                        <FaCheck className={styles.checkIcon} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  )}
                  <a
                    href={href}
                    className={`${styles.planCta} ${card.isPopular ? styles.planCtaPrimary : styles.planCtaSecondary}`}
                    data-testid={`plan-cta-${card.planId}`}
                  >
                    {card.ctaLabel}
                  </a>
                </div>
              );
            })}

            {/* Enterprise: contact-sales tier — appended after the API plans
                because it has no fixed price and isn't self-serve checkoutable. */}
            {apiPlans !== null && cards.length > 0 && (
              <div
                className={styles.planCard}
                data-aos="fade-up"
                data-aos-delay={cards.length * 100}
                data-testid="plan-card-enterprise"
              >
                <div className={styles.planHeader}>
                  <h3>{t('pricing.enterpriseName', { defaultValue: 'Enterprise' })}</h3>
                  <p className={styles.planTagline}>
                    {t('pricing.enterpriseTagline', { defaultValue: 'For large or multi-location businesses' })}
                  </p>
                  <div className={styles.priceRow}>
                    <span className={styles.price}>{t('pricing.enterprisePrice', { defaultValue: 'Custom' })}</span>
                  </div>
                </div>
                {!TEMP_CRM_PIVOT && (
                <ul className={styles.featureList}>
                  {(Array.isArray(enterpriseFeatures) ? enterpriseFeatures : []).map((f, idx) => (
                    <li key={idx}>
                      <FaCheck className={styles.checkIcon} />
                      {f}
                    </li>
                  ))}
                </ul>
                )}
                <a
                  href={enterpriseMailto}
                  className={`${styles.planCta} ${styles.planCtaSecondary}`}
                  data-testid="plan-cta-enterprise"
                >
                  {t('pricing.contactCta', { defaultValue: 'Contact Us' })}
                </a>
              </div>
            )}
          </div>

          {/* One line for the page only when every plan agrees; a mix is said on each card. */}
          {cards.length > 0 && (cards.every((c) => c.vatIncluded) || cards.every((c) => !c.vatIncluded)) && (
            <p className={styles.vatLine} data-testid="vat-line">
              {cards[0].vatIncluded
                ? t('pricing.vatLine', { defaultValue: 'Prices include VAT' })
                : t('pricing.vatLineExcluded', { defaultValue: 'Prices exclude VAT' })}
            </p>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className={styles.faqSection}>
        <div className="container">
          <h2 data-aos="fade-up">{t('pricing.faqTitle')}</h2>
          <div className={styles.faqList}>
            {Array.isArray(faqs) && faqs.map((item, i) => (
              <div key={i} className={styles.faqItem} data-aos="fade-up">
                <button
                  className={styles.faqQuestion}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  {item.q}
                  {openFaq === i ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                {openFaq === i && (
                  <div className={styles.faqAnswer}>{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.ctaSection}>
        <div className="container">
          <h2>{t('features.common.ctaTitle')}</h2>
          <p>{t('features.common.ctaSubtitle')}</p>
          <a
            href={getOnboardingUrl("cta")}
            className={styles.ctaButton}
          >
            {t('features.common.cta')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
